import { isArray, isBoolean, isEmpty, isEqual, isNil, isNumber, isObject, omit, omitBy } from 'lodash';
import { DateTime } from 'luxon';
import { CacheKeys } from 'src/constants';
import { persistValue } from 'src/lib/localStorage';
import { DEFAULT_CURRENCY, makeMonetaryAmount } from 'src/modules/currency';
import { getDefaultWatchlistId } from 'src/modules/general-helpers';
import {
  BidFilterStatus,
  BidSuccessStatus,
  BidVisibility,
  BuyableStatus,
  CountryCode,
  DateFilter,
  ExtendedUserDTO,
  MonetaryAmount,
  NormalizedTransmission,
  OfferType,
  OnlineCarsRequestDTO,
  Recommended,
  SourceType,
  TaxType,
  ValidityType,
} from 'src/modules/generated/api';
import { PotentialFilterType, bidSuccessStatusFilterable, potentialFilters } from 'src/modules/labels';
import { CarsFilterSettings } from 'src/types/CarsFilterSettings';
import { ApiCarsFilterSettings } from 'src/types/FilterSettings';
import UserRole from 'src/types/UserRoles';

import { isDefined } from '../lib';
import { BidTypes } from '../types';

import {
  AmountRangeValue,
  BidsTableNextFilterData,
  CarsTableNextFilterData,
  FilterDef,
  RangeSliderValue,
} from './types';

// TODO: Simplify transformer conditionals

const potentialInternetPriceKeys = potentialFilters.flatMap(({ value: base }) => [
  { base, key: `${base}AbsoluteMin` as keyof ApiCarsFilterSettings },
  { base, key: `${base}AbsoluteMax` as keyof ApiCarsFilterSettings },
  { base, key: `${base}RelativeMin` as keyof ApiCarsFilterSettings },
  { base, key: `${base}RelativeMax` as keyof ApiCarsFilterSettings },
]);

const omitEmpty = (data: Record<string, any>) =>
  omitBy(data, (value) => isNil(value) || value === '' || (Array.isArray(value) && value.length === 0));

/**
 * Rebuilds a slider range from the two bounds a request carries. A bound the user left blank is
 * omitted from the request, so it comes back as `''` ("unbounded on this side") rather than being
 * snapped to the slider end — otherwise reloading a search agent would drop a one-sided range or
 * silently widen it. Only when both bounds are missing is the filter itself unset.
 */
const makeRange = (min: number | undefined, max: number | undefined): RangeSliderValue =>
  min !== undefined || max !== undefined ? [min ?? '', max ?? ''] : undefined;

const makeAmountRange = (
  min: MonetaryAmount | undefined,
  max: MonetaryAmount | undefined,
): AmountRangeValue | undefined =>
  min !== undefined || max !== undefined
    ? [min !== undefined ? makeMonetaryAmount(min) : '', max !== undefined ? makeMonetaryAmount(max) : '']
    : undefined;

/**
 * CarsTableNext dropped support for "ALL" enum, as it's supported via Nil values now.
 * Exception: `offerType`, where ALL is a meaningful value.
 */
const omitLegacyAllSafe = <TValue extends string>(value: TValue | undefined): TValue | undefined =>
  value === 'ALL' ? undefined : value;

export const transformForSubmit = (data: CarsTableNextFilterData): OnlineCarsRequestDTO => {
  const { potentialFilter, startDateFilter, endDateFilter } = data;
  const defaultWatchlistId = getDefaultWatchlistId();

  // omit range slider fields and potential fields
  const primitiveFilters = omit(
    data,
    // range-slider
    'powerKw',
    'engineCo2Nedc',
    'engineCo2Wltp',
    'mileage',
    'priceDamage',
    'purchasePrice',
    'registrationDate',
    'numAuctions',
    'purchasePriceGross',
    'basePrice',
    'batteryCapacityGross',
    'potential',
    'priceChangeAbsolute',
    'priceChangeRelative',
    // potential fields
    'potentialFilterAbsoluteMin',
    'potentialFilterAbsoluteMax',
    'potentialFilterRelativeMin',
    'potentialFilterRelativeMax',
    // special fields
    'originSource',
  );

  const [powerKwMin, powerKwMax] = data.powerKw ?? [];
  const [mileageMin, mileageMax] = data.mileage ?? [];
  const [numAuctionsMin, numAuctionsMax] = data.numAuctions ?? [];
  const [engineCo2NedcMin, engineCo2NedcMax] = data.engineCo2Nedc ?? [];
  const [engineCo2WltpMin, engineCo2WltpMax] = data.engineCo2Wltp ?? [];
  const [batteryCapacityGrossMin, batteryCapacityGrossMax] = data.batteryCapacityGross ?? [];
  const [priceChangeRelativeMin, priceChangeRelativeMax] = data.priceChangeRelative ?? [];

  const splittedOriginSource = data.originSource?.map((item) => item.split(','))?.flat() ?? []; // every array of originSource item should be splitted by "," and transformed into one array

  let transformed: OnlineCarsRequestDTO = {
    ...primitiveFilters,
    startDateFrom: startDateFilter === DateFilter.Individual ? data.startDateFrom : undefined,
    startDateTo: startDateFilter === DateFilter.Individual ? data.startDateTo : undefined,
    endDateFrom: endDateFilter === DateFilter.Individual ? data.endDateFrom : undefined,
    endDateTo: endDateFilter === DateFilter.Individual ? data.endDateTo : undefined,
    search: data?.search && {
      regex: data.search.regex,
      value: data?.search?.value.trim(),
    },
    carIds: data.search.vins,
    normalizedTransmission: data.normalizedTransmission && [data.normalizedTransmission],
    searchExact: data.search?.regex,
    // Transform empty string to undefined or zero fo min values
    powerKwMin: powerKwMin || 0,
    powerKwMax: powerKwMax || undefined,
    mileageMin: mileageMin || 0,
    mileageMax: mileageMax || undefined,
    priceDamageMin: data.priceDamage?.at(0) || undefined,
    priceDamageMax: data.priceDamage?.at(1) || undefined,
    purchasePriceGrossMin: data.purchasePriceGross?.at(0) || undefined,
    purchasePriceGrossMax: data.purchasePriceGross?.at(1) || undefined,
    basePriceMin: data.basePrice?.at(0) || undefined,
    basePriceMax: data.basePrice?.at(1) || undefined,
    priceChangeAbsoluteMin: data.priceChangeAbsolute?.at(0) || undefined,
    priceChangeAbsoluteMax: data.priceChangeAbsolute?.at(1) || undefined,
    priceChangeRelativeMin: priceChangeRelativeMin === '' ? undefined : priceChangeRelativeMin,
    priceChangeRelativeMax: priceChangeRelativeMax === '' ? undefined : priceChangeRelativeMax,
    batteryCapacityGrossMin: batteryCapacityGrossMin || 0,
    batteryCapacityGrossMax: batteryCapacityGrossMax || undefined,
    numAuctionsMin: numAuctionsMin || 0,
    numAuctionsMax: numAuctionsMax || undefined,
    registrationDateMin: data.registrationDate?.[0],
    registrationDateMax: data.registrationDate?.[1],
    engineCo2NedcMin: engineCo2NedcMin || 0, // zero needs to be sent to the backend to show all cars
    engineCo2NedcMax: engineCo2NedcMax || undefined,
    engineCo2WltpMin: engineCo2WltpMin || 0,
    engineCo2WltpMax: engineCo2WltpMax || undefined,
    originSource: splittedOriginSource,
    watchListIds: data.watchlist ? [defaultWatchlistId] : [],
    recommended: data.irisRecommendation ? Recommended.Recommended : undefined,
  };

  if (potentialFilter) {
    transformed = {
      ...transformed,
      [`${potentialFilter}AbsoluteMin`]: data.potentialFilterAbsoluteMin,
      [`${potentialFilter}AbsoluteMax`]: data.potentialFilterAbsoluteMax,
      [`${potentialFilter}RelativeMin`]: data.potentialFilterRelativeMin,
      [`${potentialFilter}RelativeMax`]: data.potentialFilterRelativeMax,
    };
  }

  return omitEmpty(transformed);
};

export const transformForForm = (data: OnlineCarsRequestDTO & { id?: string }): CarsTableNextFilterData => {
  let potentialFilter: Partial<
    Pick<
      CarsFilterSettings,
      | 'potentialFilter'
      | 'potentialFilterAbsoluteMin'
      | 'potentialFilterAbsoluteMax'
      | 'potentialFilterRelativeMin'
      | 'potentialFilterRelativeMax'
    >
  > = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const potentialInternetPriceKey of potentialInternetPriceKeys) {
    if (data[potentialInternetPriceKey.key] !== undefined) {
      const potentialFilterAbsoluteMin = data[
        `${potentialInternetPriceKey.base}AbsoluteMin` as keyof typeof data
      ] as MonetaryAmount;
      const potentialFilterAbsoluteMax = data[
        `${potentialInternetPriceKey.base}AbsoluteMax` as keyof typeof data
      ] as MonetaryAmount;

      potentialFilter = {
        potentialFilter: potentialInternetPriceKey.base as PotentialFilterType,
        potentialFilterAbsoluteMin:
          potentialFilterAbsoluteMin !== undefined ? makeMonetaryAmount(potentialFilterAbsoluteMin) : undefined,
        potentialFilterAbsoluteMax:
          potentialFilterAbsoluteMax !== undefined ? makeMonetaryAmount(potentialFilterAbsoluteMax) : undefined,
        potentialFilterRelativeMin:
          data[`${potentialInternetPriceKey.base}RelativeMin` as 'potentialInternetPriceAverageRelativeMin'],
        potentialFilterRelativeMax:
          data[`${potentialInternetPriceKey.base}RelativeMax` as 'potentialInternetPriceAverageRelativeMax'],
      };

      break;
    }
  }

  // This code groups the originSource array elements to a string if the first word is the same
  // and connect them with ",". Because in the Form you select all keys of one group all at once
  const groupedOriginSource = // group the array elements to a string if the first word is the same and connect them with ","
    data.originSource
      ?.reduce((acc, item) => {
        const [firstWord] = item.split(' ');
        const lastItem = acc[acc.length - 1];

        if (lastItem && lastItem[0].startsWith(firstWord)) {
          lastItem.push(item);
        } else {
          acc.push([item]);
        }

        return acc;
      }, [] as string[][])
      .map((item) => item.join(','));

  // TODO: merge with default values instead
  const transformed: CarsTableNextFilterData = {
    id: data.id,
    search: {
      value: data.search?.value ?? '',
      regex: data.searchExact ?? data.search?.regex ?? false,
      vins: data.carIds ?? [],
    },
    startDateFilter: omitLegacyAllSafe(data.startDateFilter) ?? ('' as DateFilter),
    startDateFrom: data.startDateFrom,
    startDateTo: data.startDateTo,
    endDateFilter: omitLegacyAllSafe(data.endDateFilter) ?? ('' as DateFilter),
    endDateFrom: data.endDateFrom,
    endDateTo: data.endDateTo,
    source: data.source ?? [],
    originSource: groupedOriginSource ?? [],
    marketingChannel: data.marketingChannel ?? [],
    promotions: data.promotions ?? [],
    countryOrigin: data.countryOrigin ?? [],
    normalizedBrand: data.normalizedBrand ?? [],
    normalizedModel: data.normalizedModel ?? [],
    normalizedFuelDetail: data.normalizedFuelDetail ?? [],
    normalizedEquipmentLine: data.normalizedEquipmentLine ?? [],
    normalizedVersion: data.normalizedVersion ?? [],
    equipments: data.equipments ?? [],
    equipmentCodes: data.equipmentCodes ?? [],
    colors: data.colors ?? [],
    taxType: data.taxType ?? [],
    validity: omitLegacyAllSafe(data.validity) ?? ('' as ValidityType),
    // unlike the other filters, ALL is a real value here and must survive
    offerType: data.offerType ?? ('' as OfferType),
    buyableStatus: omitLegacyAllSafe(data.buyableStatus) ?? ('' as BuyableStatus),
    ratingMin: data.ratingMin ?? 0,
    hasUserComment: data.hasUserComment ?? false,
    anyOtherUserRatingMin: data.anyOtherUserRatingMin ?? 0,
    hasAnyOtherUserComment: data.hasAnyOtherUserComment ?? false,
    currentBidState: omitLegacyAllSafe(data.currentBidState) ?? ('' as BidFilterStatus),
    expiredBidState: omitLegacyAllSafe(data.expiredBidState) ?? ('' as BidFilterStatus),
    normalizedTransmission: omitLegacyAllSafe(data.normalizedTransmission?.[0]) ?? ('' as NormalizedTransmission),
    powerKw: makeRange(data.powerKwMin, data.powerKwMax),
    engineCo2Nedc:
      data.engineCo2Min !== undefined || data.engineCo2Max !== undefined
        ? makeRange(data.engineCo2Min, data.engineCo2Max)
        : makeRange(data.engineCo2NedcMin, data.engineCo2NedcMax),
    engineCo2Wltp:
      data.engineCo2Min !== undefined || data.engineCo2Max !== undefined
        ? makeRange(data.engineCo2Min, data.engineCo2Max)
        : makeRange(data.engineCo2WltpMin, data.engineCo2WltpMax),
    mileage: makeRange(data.mileageMin, data.mileageMax),
    numAuctions: makeRange(data.numAuctionsMin, data.numAuctionsMax),
    registrationDate: [
      data.registrationDateMin ?? DateTime.fromISO('1990-01-01').toISODate(),
      data.registrationDateMax ?? DateTime.now().plus({ month: 1 }).endOf('month').toISODate(),
    ],
    priceDamage: makeAmountRange(data.priceDamageMin, data.priceDamageMax) ?? [
      makeMonetaryAmount(0, DEFAULT_CURRENCY),
      makeMonetaryAmount(25_000, DEFAULT_CURRENCY),
    ],
    purchasePriceGross: makeAmountRange(data.purchasePriceGrossMin, data.purchasePriceGrossMax) ?? [
      makeMonetaryAmount(0, DEFAULT_CURRENCY),
      makeMonetaryAmount(900_000, DEFAULT_CURRENCY),
    ],
    bodyType: data.bodyType ?? [],
    damageType: data.damageType ?? [],
    basePrice: makeAmountRange(data.basePriceMin, data.basePriceMax) ?? [
      makeMonetaryAmount(0, DEFAULT_CURRENCY),
      makeMonetaryAmount(600_000, DEFAULT_CURRENCY),
    ],
    // Price change filters stay unset until the user touches them, so nothing is sent by default.
    priceChangeAbsolute: makeAmountRange(data.priceChangeAbsoluteMin, data.priceChangeAbsoluteMax),
    priceChangeRelative: makeRange(data.priceChangeRelativeMin, data.priceChangeRelativeMax),
    batteryCapacityGross: makeRange(data.batteryCapacityGrossMin, data.batteryCapacityGrossMax),
    kvps: data.kvps ?? [],
    watchlist: !isEmpty(data.watchListIds),
    irisRecommendation: data.recommended === Recommended.Recommended,
    potentialFilter: potentialFilter.potentialFilter ?? null,
    ...potentialFilter,
  };

  return transformed;
};

export const getActiveCount = (filterDef: FilterDef[], values: Record<string, unknown>, defaults: Object): number => {
  let counter = 0;
  // count all filters besides sliders that are on default
  filterDef.forEach((def) => {
    const value = values[def.name as keyof typeof values];
    const defaultValue = defaults?.[def.name as keyof typeof defaults];

    const isSimpleCurrencySlider = isArray(value) && isObject(value[0]);
    const isMinMaxCurrencySlider = isObject(value) && !isArray(value);

    // ALL is the neutral offer type, it restricts nothing and never counts as active
    if (def.name === 'offerType' && value === OfferType.All) return;

    // A range whose bounds are all blank restricts nothing. Both CurrencySliderInput and RangeSlider
    // settle on ['', ''] rather than undefined once cleared, which would otherwise be counted as
    // active even though no chip is shown and nothing is sent to the API.
    if (isArray(value) && !isEmpty(value) && value.every((bound) => bound === '')) return;

    if (isSimpleCurrencySlider || isMinMaxCurrencySlider || isBoolean(value)) {
      // sliders and true/false (comment) count only if they are not default
      if (!isEqual(defaultValue, value)) {
        counter += 1;
      }
    } else if (isNumber(value)) {
      // for ratings
      if (value > 0) counter += 1;
    } else if (!isEmpty(value)) counter += 1;
  });

  return counter;
};

const firstCallDefaultCountries = [
  CountryCode.At,
  CountryCode.It,
  CountryCode.Es,
  CountryCode.Pt,
  CountryCode.Sk,
  CountryCode.Bg,
  CountryCode.De,
];

export const getDefaultSource = ({
  sources,
  country,
  allowed,
  isAdmin,
}: {
  sources?: SourceType[];
  country?: CountryCode;
  allowed?: SourceType[];
  isAdmin: boolean;
}): SourceType[] => {
  if (isAdmin) {
    return [
      sources !== undefined && sources.includes(SourceType.Vwfs) && SourceType.Vwfs,
      sources !== undefined &&
        sources.includes(SourceType.RealFirstCallOther) &&
        country !== undefined &&
        firstCallDefaultCountries.includes(country) &&
        SourceType.RealFirstCallOther,
      sources !== undefined &&
        sources.includes(SourceType.RealFirstCallOwn) &&
        country !== undefined &&
        country === CountryCode.De &&
        SourceType.RealFirstCallOwn,
      sources !== undefined && sources.includes(SourceType.SpotdealsAllmobil) && SourceType.SpotdealsAllmobil,
      sources !== undefined && sources.includes(SourceType.VwfsDkVirt) && SourceType.VwfsDkVirt,
      sources !== undefined && sources.includes(SourceType.VwfsItVirt) && SourceType.VwfsItVirt,
      sources !== undefined && sources.includes(SourceType.VwfsSkVirt) && SourceType.VwfsSkVirt,
      sources !== undefined && sources.includes(SourceType.VwfsPtVirt) && SourceType.VwfsPtVirt,
      sources !== undefined && sources.includes(SourceType.VwfsFrVirt) && SourceType.VwfsFrVirt,
      sources !== undefined && sources.includes(SourceType.Vvj) && SourceType.Vvj,
      sources !== undefined && sources.includes(SourceType.VwfsCzVirt) && SourceType.VwfsCzVirt,
    ].filter(isDefined);
  }
  return allowed?.filter((source) => sources?.includes(source)).filter((s) => s !== SourceType.AudiGws) ?? [];
};

export const patchRegistrationDate = (date: number | string) => {
  // Previous `registrationDate` filter values might only contain the year as INT.
  // In order to provide a smooth upgrade experience we upsert the prev state to
  // the current type.
  // 1) Year (number) --> ISO Date (string)
  if (typeof date === 'number') {
    return DateTime.now().set({ year: date }).toISODate();
  }
  return date;
};

export const transformPersistedFilter = (prev: CarsTableNextFilterData) => {
  if (!prev) {
    return prev;
  }

  const { registrationDate: [minRegistrationDate, maxRegistrationDate] = [] } = prev;

  return {
    ...prev,
    registrationDate:
      minRegistrationDate !== undefined && maxRegistrationDate !== undefined
        ? ([patchRegistrationDate(minRegistrationDate), patchRegistrationDate(maxRegistrationDate)] as [string, string])
        : undefined,
    basePrice: prev.basePrice ?? [
      makeMonetaryAmount(0, DEFAULT_CURRENCY),
      makeMonetaryAmount(600_000, DEFAULT_CURRENCY),
    ],
  };
};

export const getDefaultFilter = ({
  sources,
  user,
  admin = false,
  canFilterOfferType = false,
}: {
  sources?: SourceType[];
  user?: ExtendedUserDTO;
  admin?: boolean;
  canFilterOfferType?: boolean;
} = {}): CarsTableNextFilterData => ({
  search: {
    value: '',
    regex: false,
    vins: [],
  },
  source: getDefaultSource({ sources, country: user?.dealer?.country, allowed: user?.allowedSources, isAdmin: admin }),
  promotions: [],
  marketingChannel: [],
  registrationDate: ['1990-01-01', DateTime.now().plus({ month: 1 }).endOf('month').toFormat('yyyy-MM-dd')],
  mileage: [0, 250000],
  engineCo2Wltp: [0, 400],
  engineCo2Nedc: [0, 400],
  numAuctions: [0, 50],
  powerKw: [0, 500],
  priceDamage: [makeMonetaryAmount(0, DEFAULT_CURRENCY), makeMonetaryAmount(25_000, DEFAULT_CURRENCY)],
  purchasePriceGross: [makeMonetaryAmount(0, DEFAULT_CURRENCY), makeMonetaryAmount(1000000, DEFAULT_CURRENCY)],
  countryOrigin: [],
  normalizedBrand: [],
  normalizedModel: [],
  normalizedFuelDetail: [],
  normalizedTransmission: '' as NormalizedTransmission,
  normalizedEquipmentLine: [],
  normalizedVersion: [],
  equipments: [],
  equipmentCodes: [],
  colors: [],
  taxType: [TaxType.Regelbesteuert],
  validity: ValidityType.Current,
  offerType: canFilterOfferType ? OfferType.All : ('' as OfferType),
  currentBidState: '' as BidFilterStatus,
  expiredBidState: '' as BidFilterStatus,
  ratingMin: 0,
  anyOtherUserRatingMin: 0,
  buyableStatus: '' as BuyableStatus,
  hasUserComment: false,
  hasAnyOtherUserComment: false,
  originSource: [],
  potentialFilter: null,
  startDateFilter: '' as DateFilter,
  startDateFrom: DateTime.now().startOf('day').toISO(),
  startDateTo: DateTime.now().plus({ hour: 1 }).startOf('hour').toISO(),
  endDateFilter: '' as DateFilter,
  endDateFrom: DateTime.now().startOf('day').toISO(),
  endDateTo: DateTime.now().endOf('day').toISO(),
  bodyType: [],
  damageType: [],
  basePrice: [makeMonetaryAmount(0, DEFAULT_CURRENCY), makeMonetaryAmount(600_000, DEFAULT_CURRENCY)],
  batteryCapacityGross: [0, 150],
  kvps: [],
  watchlist: false,
  irisRecommendation: false,
  priceChangeAbsolute: undefined,
  priceChangeRelative: undefined,
});

export const getDefaultBidsFilter = ({
  sources,
  user,
  bidType,
  roles = [UserRole.DEALER],
  canFilterOfferType = false,
}: {
  sources?: SourceType[];
  user?: ExtendedUserDTO;
  bidType?: BidTypes;
  roles?: string[];
  canFilterOfferType?: boolean;
} = {}): BidsTableNextFilterData => ({
  carsRequest: {
    search: {
      value: '',
      regex: false,
      vins: [],
    },
    source: getDefaultSource({
      sources,
      country: user?.dealer?.country,
      allowed: user?.allowedSources,
      isAdmin: user?.roles?.includes(UserRole.ADMIN) ?? false,
    }),
    promotions: [],
    marketingChannel: [],
    registrationDate: ['1990-01-01', DateTime.now().plus({ month: 1 }).endOf('month').toFormat('yyyy-MM-dd')],
    mileage: [0, 250000],
    engineCo2Wltp: [0, 400],
    engineCo2Nedc: [0, 400],
    numAuctions: [0, 50],
    powerKw: [0, 500],
    priceDamage: [makeMonetaryAmount(0, DEFAULT_CURRENCY), makeMonetaryAmount(25_000, DEFAULT_CURRENCY)],
    purchasePriceGross: [makeMonetaryAmount(0, DEFAULT_CURRENCY), makeMonetaryAmount(1000000, DEFAULT_CURRENCY)],
    countryOrigin: [],
    normalizedBrand: [],
    normalizedModel: [],
    normalizedFuelDetail: [],
    normalizedTransmission: '' as NormalizedTransmission,
    normalizedEquipmentLine: [],
    normalizedVersion: [],
    equipments: [],
    equipmentCodes: [],
    colors: [],
    taxType: [],
    validity: '' as ValidityType,
    offerType: canFilterOfferType ? OfferType.All : ('' as OfferType),
    currentBidState: '' as BidFilterStatus,
    expiredBidState: '' as BidFilterStatus,
    ratingMin: 0,
    anyOtherUserRatingMin: 0,
    buyableStatus: BuyableStatus.All,
    hasUserComment: false,
    hasAnyOtherUserComment: false,
    originSource: [],
    potentialFilter: null,
    bodyType: [],
    damageType: [],
    basePrice: undefined,
    batteryCapacityGross: [0, 150],
    kvps: [],
    watchlist: false,
    irisRecommendation: false,
    priceChangeAbsolute: undefined,
    priceChangeRelative: undefined,
  },
  bidSuccessStates:
    // eslint-disable-next-line no-nested-ternary
    bidType === BidTypes.Past
      ? bidSuccessStatusFilterable.filter(
          (s) => s !== BidSuccessStatus.Cancelled || user?.roles?.includes(UserRole.ADMIN),
        )
      : bidType === BidTypes.Pending
        ? [BidSuccessStatus.Scheduled]
        : [BidSuccessStatus.Bought],
  bidVisibility: user?.roles?.includes(UserRole.ADMIN) ? BidVisibility.All : BidVisibility.User,
  biddingTimeFilter: {
    timeType: DateFilter.All,
    timeFrom: DateTime.now().startOf('year').toISO(),
    timeTo: DateTime.now().toISO(),
  },
  boughtTimeFilter: {
    timeType: DateFilter.All,
    timeFrom: DateTime.now().startOf('year').toISO(),
    timeTo: DateTime.now().toISO(),
  },
  bidCreatorDealerCountry:
    roles.some((role) => role === UserRole.ADMIN || role === UserRole.COUNTRYMANAGER) && user?.dealer?.country
      ? [user.dealer.country]
      : [],
  bidCreatorDealerId:
    roles.some(
      (role) => role === UserRole.ADMIN || role === UserRole.COUNTRYMANAGER || role === UserRole.DEALERADMIN,
    ) && user?.dealer?.id
      ? [user.dealer.id]
      : [],
  bidCreatorUserId:
    roles.some(
      (role) => role === UserRole.ADMIN || role === UserRole.COUNTRYMANAGER || role === UserRole.DEALERADMIN,
    ) && user?.user?.id
      ? [`${user.user.authenticationProvider}-${user.user.id}`]
      : [],
  biddingKvps: [],
  showBidsWithAutomation: [],
});

export function persistFilter(filter: CarsTableNextFilterData) {
  persistValue(CacheKeys.carsTableNextFilter, filter);
}

export function persistBidsPendingFilter(filter: BidsTableNextFilterData) {
  persistValue(CacheKeys.bidsPendingTableNextFilter, filter);
}

export function persistBidsPastFilter(filter: BidsTableNextFilterData) {
  persistValue(CacheKeys.bidsPastTableNextFilter, filter);
}

export function persistBidsOrderedFilter(filter: BidsTableNextFilterData) {
  persistValue(CacheKeys.bidsOrderedTableNextFilter, filter);
}
